import { useTranslation } from "react-i18next";
import { Button, Checkbox, Icon, Modal, TextArea } from "semantic-ui-react";

import { SortableList } from "modules/NoteSuggestions/components";
import "modules/NoteSuggestions/components/NotesSuggestionModal/NoteSuggestionsModal.scss";
import { NoteSuggestionTypeLookupMap, useNoteSuggestionsModal } from "modules/NoteSuggestions/hooks";

import type { NOTE_SUGGESTION_TYPES } from "models";
import type { SortableItems } from "modules/NoteSuggestions/components";
import type { NoteSuggestionTypeWithSuggestions } from "modules/NoteSuggestions/hooks";

export type NoteModalProps = {
  openedModalNoteType: NOTE_SUGGESTION_TYPES | null;
  noteTypesWithSuggestions: NoteSuggestionTypeWithSuggestions[];
  closeModal: () => void;
};

export const NoteSuggestionsModal = ({ openedModalNoteType, closeModal, noteTypesWithSuggestions }: NoteModalProps) => {
  const { t } = useTranslation();

  const { items, handleOnSave, handleVisibleInNote, handleAddItem, handleRemoveItem, handleContentEdit, noteVisibility, setItems } = useNoteSuggestionsModal({
    openedModalNoteType,
    closeModal,
    noteTypesWithSuggestions
  });

  const noteTypeLookup = NoteSuggestionTypeLookupMap.get(openedModalNoteType);
  if (!noteTypeLookup) return null;

  const handleResortNotesSuggestions = (items: SortableItems[]) => setItems(items.map((item, idx) => ({ ...item, ordering: idx + 1 })));

  return (
    <Modal open={!!openedModalNoteType} className="NoteSuggestionModal">
      <Modal.Header>
        <div className="noteSuggestionHeader">
          <Icon className={`${noteTypeLookup.icon} green`} />
          <h3>{t(noteTypeLookup.translation_key) || noteTypeLookup.default_message}</h3>
        </div>
      </Modal.Header>
      <Modal.Content>
        <SortableList
          items={items}
          onChange={handleResortNotesSuggestions}
          renderItem={item => (
            <SortableList.Item {...item}>
              <div className="input-wrapper">
                <TextArea
                  className="note-text-area"
                  rows={3}
                  defaultValue={item.content}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleContentEdit(item.id, e.target.value)}
                />
                <SortableList.DragHandle id={item.id} />
              </div>
              <Button onClick={() => handleRemoveItem(item.id)} className="delete-note-suggestion">
                <Icon className="trash red" />
              </Button>
            </SortableList.Item>
          )}
        />
        <div className="note-controls">
          <div onClick={handleAddItem} className="add-note-button">
            <div className="plus-wrapper">
              <Icon className="plus" />
            </div>
            <span>{t("v8_add_answer", "Add answer")}</span>
          </div>
          <div className="note-toggle-wrapper">
            <label htmlFor="note-visibility">
              <b>{t("v8_visible_in_note", "Visible in note")}</b>
            </label>
            <Checkbox name="note-visibility" toggle onChange={handleVisibleInNote} checked={noteVisibility} />
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions className="note-modal-actions">
        <Button onClick={closeModal} basic>
          <span>{t("v8_cancel", "Cancel")}</span>
          <Icon className="xmark" />
        </Button>
        <Button onClick={handleOnSave} color="green">
          <span>{t("v8_save", "Save")}</span>
          <Icon className="check" />
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
